import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { DragOverEvent, useDndMonitor } from '@dnd-kit/core';

interface DataTableDropIndicatorProps {
  containerRef: RefObject<HTMLElement | null>;
  items: string[];
  offsetLeft: number;
  offsetRight: number;
}

interface DragData {
  contentX: number;
  contentY: number;
}

const INDICATOR_WIDTH = 3;

const CSS_VAR_DISPLAY = '--drop-indicator-display';
const CSS_VAR_LEFT = '--drop-indicator-left';
const CSS_VAR_TOP = '--drop-indicator-top';
const CSS_VAR_HEIGHT = '--drop-indicator-height';

const CSS_VARS_TO_SET = [
  CSS_VAR_DISPLAY,
  CSS_VAR_LEFT,
  CSS_VAR_TOP,
  CSS_VAR_HEIGHT,
];

export const DataTableDropIndicator = ({
  containerRef,
  items,
  offsetLeft,
  offsetRight,
}: DataTableDropIndicatorProps) => {
  const [visible, setVisible] = useState(false);
  const dragDataRef = useRef<DragData | null>(null);

  const applyPosition = useCallback(() => {
    const containerRefElement = containerRef.current;
    const data = dragDataRef.current;
    if (!containerRefElement || !data) return;

    const { scrollLeft, clientWidth, scrollHeight } = containerRefElement;

    const minLeft = scrollLeft + offsetLeft;
    const maxLeft = scrollLeft + clientWidth - offsetRight - INDICATOR_WIDTH;

    if (maxLeft < minLeft)
      return containerRefElement.style.setProperty(CSS_VAR_DISPLAY, 'none');

    const unclampedLeft = data.contentX - INDICATOR_WIDTH / 2;
    const left = Math.min(Math.max(unclampedLeft, minLeft), maxLeft);
    const height = Math.max(0, scrollHeight - data.contentY);

    const variables = {
      [CSS_VAR_DISPLAY]: 'block',
      [CSS_VAR_LEFT]: `${left}px`,
      [CSS_VAR_TOP]: `${data.contentY}px`,
      [CSS_VAR_HEIGHT]: `${height}px`,
    };

    CSS_VARS_TO_SET.forEach(cssVar => {
      containerRefElement.style.setProperty(
        cssVar,
        variables[cssVar as keyof typeof variables]
      );
    });
  }, [containerRef, offsetLeft, offsetRight]);

  const clearCssVariables = useCallback(() => {
    const containerRefElement = containerRef.current;
    if (!containerRefElement) return;

    CSS_VARS_TO_SET.forEach(cssVar => {
      containerRefElement.style.removeProperty(cssVar);
    });
  }, [containerRef]);

  const extractDragData = useCallback(
    (event: DragOverEvent): DragData | null => {
      const { active, over } = event;
      const containerRefElement = containerRef.current;

      if (!active || !over || !containerRefElement) return null;

      const activeId = `${active.id}`;
      const overId = `${over.id}`;
      const activeIndex = items.indexOf(activeId);
      const overIndex = items.indexOf(overId);

      if (activeIndex === -1 || overIndex === -1 || activeId === overId)
        return null;

      const overRect = over.rect;
      if (!overRect) return null;

      const containerRect = containerRefElement.getBoundingClientRect();
      const placeAfterOver = overIndex > activeIndex;

      const offsetX =
        overRect.left - containerRect.left + containerRefElement.scrollLeft;
      const contentX = placeAfterOver ? offsetX + overRect.width : offsetX;
      const contentY =
        overRect.top - containerRect.top + containerRefElement.scrollTop;

      return { contentX, contentY };
    },
    [containerRef, items]
  );

  const handleClear = useCallback(() => {
    dragDataRef.current = null;
    setVisible(false);
    clearCssVariables();

    const containerRefElement = containerRef.current;
    if (containerRefElement) containerRefElement.style.overflowY = '';
  }, [clearCssVariables, containerRef]);

  useDndMonitor({
    onDragStart: () => {
      const containerRefElement = containerRef.current;
      if (containerRefElement) containerRefElement.style.overflowY = 'hidden';
    },
    onDragOver: event => {
      const data = extractDragData(event);
      if (!data) {
        dragDataRef.current = null;
        setVisible(false);
        clearCssVariables();

        return;
      }

      dragDataRef.current = data;
      setVisible(true);
      applyPosition();
    },
    onDragEnd: handleClear,
    onDragCancel: handleClear,
  });

  useEffect(() => {
    if (visible) applyPosition();
  }, [visible, applyPosition]);

  useEffect(() => {
    const containerRefElement = containerRef.current;
    if (!containerRefElement) return;

    const onScroll = () => {
      if (dragDataRef.current) applyPosition();
    };

    containerRefElement.addEventListener('scroll', onScroll, { passive: true });

    return () => containerRefElement.removeEventListener('scroll', onScroll);
  }, [containerRef, applyPosition]);

  if (!visible) return null;

  return (
    <div
      aria-hidden
      className="bg-secondary pointer-events-none absolute z-50 w-[3px]"
      style={{
        display: 'var(--drop-indicator-display, none)',
        left: 'var(--drop-indicator-left)',
        top: 'var(--drop-indicator-top)',
        height: 'var(--drop-indicator-height)',
      }}
    />
  );
};
